import { useState, useEffect, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
/**
 * 从管理端 Presenter 导入真实接口调用
 * Presenter 封装了 postHttp/getHttp → Promise，并适配返回字段
 */
import {
  getModelTokenUsage,
  getUserModelTokenUsage,
  getChannelModelTokenUsage,
  getUsers,
  getPortalPresence,
  addUser,
  deleteUser,
  updateUserPwd,
  updateUsernameAlias,
  resetNonAdminPasswords,
  getResetNonAdminPwdStatus,
  getAgents,
  addAgent,
  updateAgent,
  deleteAgent,
  getAnnouncements,
  addAnnouncement,
  updateAnnouncement,
  deleteAnnouncement
} from '../pages/AdminDashboard/AdminDashboardPresenter';

// 默认近一个月时间范围
const defaultDateRange = [dayjs().subtract(1, 'month').startOf('day'), dayjs().endOf('day')];

const normalizeIdArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === null || value === undefined || value === '') return [];
  return String(value)
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
};

/**
 * 模型维度Token统计 Hook
 */
export const useModelTokenStats = (messageApi) => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);

  const userOptions = useMemo(() => {
    return [...new Set(rawData.map(item => item.username))].sort();
  }, [rawData]);

  const modelOptions = useMemo(() => {
    if (!Array.isArray(rawData)) return [];
    return [...new Set(rawData.map(item => item.model_name))].sort();
  }, [rawData]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(rawData)) return [];
    let result = rawData;
    if (selectedUsers.length > 0) {
      result = result.filter(item => selectedUsers.includes(item.username));
    }
    if (selectedModels.length > 0) {
      result = result.filter(item => selectedModels.includes(item.model_name));
    }
    return result;
  }, [rawData, selectedUsers, selectedModels]);

  const handleSearch = useCallback(async () => {
    if (!dateRange || dateRange.length < 2) {
      messageApi.warning('请选择时间范围');
      return;
    }
    setLoading(true);
    try {
      const params = {
        start_time: dateRange[0].unix(),
        end_time: dateRange[1].unix(),
      };
      const res = await getModelTokenUsage(params);
      if (res && res.status === 200 && Array.isArray(res.data)) {
        setRawData(res.data);
      } else {
        setRawData([]);
        messageApi.error(res?.msg || '查询失败');
      }
    } catch (err) {
      setRawData([]);
      messageApi.error('查询失败');
    } finally {
      setLoading(false);
    }
  }, [dateRange, messageApi]);

  const handleRefresh = handleSearch;

  return {
    rawData,
    loading,
    dateRange,
    setDateRange,
    selectedUsers,
    setSelectedUsers,
    selectedModels,
    setSelectedModels,
    userOptions,
    modelOptions,
    filteredData,
    handleSearch,
    handleRefresh,
  };
};

/**
 * 用户维度Token统计 Hook
 */
export const useUserTokenStats = (messageApi) => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const userOptions = useMemo(() => {
    return [...new Set(rawData.map(item => item.username))].sort();
  }, [rawData]);

  const filteredData = useMemo(() => {
    let result = rawData;
    if (selectedUsers.length > 0) {
      result = result.filter(item => selectedUsers.includes(item.username));
    }
    return result;
  }, [rawData, selectedUsers]);

  const handleSearch = useCallback(async () => {
    if (!dateRange || dateRange.length < 2) {
      messageApi.warning('请选择时间范围');
      return;
    }
    setLoading(true);
    try {
      const params = {
        start_time: dateRange[0].unix(),
        end_time: dateRange[1].unix(),
      };
      const res = await getUserModelTokenUsage(params);
      if (res.status === 200) {
        setRawData(res.data || []);
      }
    } catch (err) {
      messageApi.error('查询失败');
    } finally {
      setLoading(false);
    }
  }, [dateRange, messageApi]);

  const handleRefresh = handleSearch;

  return {
    rawData,
    loading,
    dateRange,
    setDateRange,
    selectedUsers,
    setSelectedUsers,
    userOptions,
    filteredData,
    handleSearch,
    handleRefresh,
  };
};

/**
 * 渠道模型Token统计 Hook
 */
export const useChannelTokenStats = (messageApi) => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [selectedModels, setSelectedModels] = useState([]);

  const modelOptions = useMemo(() => {
    return [...new Set(rawData.map(item => item.model_name))].sort();
  }, [rawData]);

  const filteredData = useMemo(() => {
    let result = rawData;
    if (selectedModels.length > 0) {
      result = result.filter(item => selectedModels.includes(item.model_name));
    }
    return result;
  }, [rawData, selectedModels]);

  const handleSearch = useCallback(async () => {
    if (!dateRange || dateRange.length < 2) {
      messageApi.warning('请选择时间范围');
      return;
    }
    setLoading(true);
    try {
      const params = {
        start_time: dateRange[0].unix(),
        end_time: dateRange[1].unix(),
      };
      const res = await getChannelModelTokenUsage(params);
      if (res.status === 200) {
        setRawData(res.data || []);
      }
    } catch (err) {
      messageApi.error('查询失败');
    } finally {
      setLoading(false);
    }
  }, [dateRange, messageApi]);

  const handleRefresh = handleSearch;

  return {
    rawData,
    loading,
    dateRange,
    setDateRange,
    selectedModels,
    setSelectedModels,
    modelOptions,
    filteredData,
    handleSearch,
    handleRefresh,
  };
};

/**
 * 用户管理 Hook
 */
export const getEffectiveUsername = (user = {}) => {
  const alias = String(user.username_alias || '').trim();
  if (alias) return alias;
  const username = String(user.username || '');
  return username.includes('@') ? username.split('@')[0] : username;
};

export const useUserManagement = (messageApi) => {
  const [users, setUsers] = useState([]);
  const [onlineCount, setOnlineCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedUsernames, setSelectedUsernames] = useState([]);
  const [addUserModalVisible, setAddUserModalVisible] = useState(false);
  const [addUserSubmitting, setAddUserSubmitting] = useState(false);
  const [editPwdModalVisible, setEditPwdModalVisible] = useState(false);
  const [editPwdSubmitting, setEditPwdSubmitting] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [aliasModalVisible, setAliasModalVisible] = useState(false);
  const [aliasSubmitting, setAliasSubmitting] = useState(false);
  const [editingAliasUser, setEditingAliasUser] = useState(null);

  const usernameOptions = useMemo(() => {
    return [...new Set(users.map(item => item.username))].sort();
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (!selectedUsernames || selectedUsernames.length === 0) return users;
    return users.filter(item => selectedUsernames.includes(item.username));
  }, [users, selectedUsernames]);

  const loadPresence = useCallback(async () => {
    if (document.visibilityState === 'hidden') return;
    try {
      const presence = await getPortalPresence();
      const ok = presence.status === 200;
      setOnlineCount(ok && Number.isFinite(Number(presence.data?.online_count))
        ? Number(presence.data.online_count) : null);
      // 在线状态每 60 秒刷新时同步更新表格列，无需重新拉取整份用户列表
      const onlineIds = presence.data?.online_user_ids;
      if (ok && Array.isArray(onlineIds)) {
        const onlineSet = new Set(onlineIds.map(String));
        setUsers(prev => prev.map(user => (
          Boolean(user.online) === onlineSet.has(String(user.user_id))
            ? user
            : { ...user, online: onlineSet.has(String(user.user_id)) }
        )));
      }
    } catch {
      setOnlineCount(null);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getUsers();
      if (res.status === 200) setUsers(res.users || []);
      await loadPresence();
    } catch (err) {
      messageApi.error('加载用户列表失败');
    } finally {
      setLoading(false);
    }
  }, [messageApi, loadPresence]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') loadPresence(); };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(onVisible, 60000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [loadPresence]);

  const summary = useMemo(() => ({
    total: users.length,
    admins: users.filter(user => user.is_admin === true || user.is_admin === 'true' || user.is_admin === 1 || user.is_admin === '1').length,
    regular: users.filter(user => !(user.is_admin === true || user.is_admin === 'true' || user.is_admin === 1 || user.is_admin === '1')).length,
    online: onlineCount,
  }), [users, onlineCount]);

  const handleAddUser = async (values, form) => {
    try {
      setAddUserSubmitting(true);
      const res = await addUser(values);
      if (res.status === 200) {
        messageApi.success('用户添加成功');
        setAddUserModalVisible(false);
        form.resetFields();
        loadUsers();
      } else {
        messageApi.error(res.msg || '添加失败');
      }
    } catch (err) {
      if (err.errorFields) return;
      messageApi.error('添加用户失败');
    } finally {
      setAddUserSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const res = await deleteUser(userId);
      if (res.status === 200) {
        messageApi.success('用户已删除');
        loadUsers();
      }
    } catch (err) {
      messageApi.error('删除失败');
    }
  };

  const handleOpenEditPwdModal = (record) => {
    setEditingUser(record);
    setEditPwdModalVisible(true);
  };

  const handleOpenAliasModal = (record) => {
    setEditingAliasUser(record);
    setAliasModalVisible(true);
  };

  const handleSaveAlias = async (values, form) => {
    try {
      setAliasSubmitting(true);
      const alias = String(values.username_alias || '').trim();
      const res = await updateUsernameAlias({
        user_id: editingAliasUser.user_id,
        username_alias: alias,
      });
      if (res.status === 200) {
        messageApi.success('短用户名已更新');
        setAliasModalVisible(false);
        form.resetFields();
        setEditingAliasUser(null);
        loadUsers();
      } else {
        messageApi.error(res.msg || '更新失败');
      }
    } catch (err) {
      if (err.errorFields) return;
      messageApi.error('更新短用户名失败');
    } finally {
      setAliasSubmitting(false);
    }
  };

  const handleUpdatePwd = async (values, form) => {
    try {
      setEditPwdSubmitting(true);
      const res = await updateUserPwd({ user_id: editingUser.user_id, pwd: values.new_pwd });
      if (res.status === 200) {
        messageApi.success('密码更新成功');
        setEditPwdModalVisible(false);
        form.resetFields();
        setEditingUser(null);
        loadUsers();
      } else {
        messageApi.error(res.msg || '更新失败');
      }
    } catch (err) {
      if (err.errorFields) return;
      messageApi.error('更新密码失败');
    } finally {
      setEditPwdSubmitting(false);
    }
  };

  // 一键重置所有非管理员密码：启动后台任务并轮询状态
  const [resetPwdRunning, setResetPwdRunning] = useState(false);

  const handleResetNonAdminPasswords = async () => {
    try {
      const res = await resetNonAdminPasswords();
      if (res.status !== 200) {
        messageApi.error(res.msg || '启动重置失败');
        return;
      }
      messageApi.success('已开始重置，正在后台执行，请稍候…');
      setResetPwdRunning(true);
      const startedAt = Date.now();
      const timer = setInterval(async () => {
        try {
          const statusRes = await getResetNonAdminPwdStatus();
          if (statusRes.status !== 200 || !statusRes.data) {
            clearInterval(timer);
            setResetPwdRunning(false);
            return;
          }
          const data = statusRes.data;
          if (!data.running) {
            clearInterval(timer);
            setResetPwdRunning(false);
            const failedTip = data.failed_count ? `，失败 ${data.failed_count} 个（详见后端日志）` : '';
            messageApi.success(`重置完成：非管理员 ${data.reset_count || 0} 个，跳过管理员 ${data.admin_skipped_count || 0} 个${failedTip}`);
            loadUsers();
            return;
          }
          if (Date.now() - startedAt > 15 * 60 * 1000) {
            clearInterval(timer);
            setResetPwdRunning(false);
            messageApi.warning('重置仍在后台进行，稍后刷新用户列表查看结果');
          }
        } catch (err) {
          clearInterval(timer);
          setResetPwdRunning(false);
        }
      }, 3000);
    } catch (err) {
      messageApi.error('启动重置失败');
    }
  };

  return {
    users,
    summary,
    filteredUsers,
    loading,
    selectedUsernames,
    setSelectedUsernames,
    usernameOptions,
    addUserModalVisible,
    setAddUserModalVisible,
    addUserSubmitting,
    editPwdModalVisible,
    setEditPwdModalVisible,
    editPwdSubmitting,
    editingUser,
    loadUsers,
    handleAddUser,
    handleDeleteUser,
    handleOpenEditPwdModal,
    handleUpdatePwd,
    aliasModalVisible,
    setAliasModalVisible,
    aliasSubmitting,
    editingAliasUser,
    handleOpenAliasModal,
    handleSaveAlias,
    resetPwdRunning,
    handleResetNonAdminPasswords,
  };
};

/**
 * AI工具管理 Hook
 */
export const useAgentManagement = (messageApi) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedNames, setSelectedNames] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [submitting, setSubmitting] = useState(false);

  const nameOptions = useMemo(() => {
    return [...new Set(agents.map(item => item.agent_name))].sort();
  }, [agents]);

  const filteredAgents = useMemo(() => {
    let result = agents;
    if (selectedNames.length > 0) {
      result = result.filter(item => selectedNames.includes(item.agent_name));
    }
    if (selectedStatus.length > 0) {
      result = result.filter(item => selectedStatus.includes(item.status));
    }
    return result;
  }, [agents, selectedNames, selectedStatus]);

  const loadAgents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAgents({});
      if (res.status === 200) {
        setAgents(res.agents || []);
      }
    } catch (err) {
      messageApi.error('加载AI工具列表失败');
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => { loadAgents(); }, [loadAgents]);

  const handleOpenModal = (mode, record = null) => {
    setModalMode(mode);
    if (mode === 'edit' && record) {
      // record will be set by form.setFieldsValue in component
    }
    setModalVisible(true);
    return record;
  };

  const handleSubmitAgent = async (values, form, mode) => {
    try {
      setSubmitting(true);
      const normalizedValues = {
        ...values,
        is_support_email: values.is_support_email === false ? 0 : 1,
        agent_icon: values.agent_icon || '', // 未选/选“自动”时统一传空字符串，空=回退自动图标
      };
      let res;
      if (mode === 'add') {
        const submitValues = {
          ...normalizedValues,
          dep_ids: normalizeIdArray(values.dep_ids),
          role_ids: normalizeIdArray(values.role_ids),
        };
        res = await addAgent(submitValues);
      } else {
        res = await updateAgent(normalizedValues);
      }
      if (res.status === 200) {
        messageApi.success(mode === 'add' ? '工具添加成功' : '工具更新成功');
        setModalVisible(false);
        form.resetFields();
        loadAgents();
      } else {
        messageApi.error(res.msg || '操作失败');
      }
    } catch (err) {
      if (err.errorFields) return;
      messageApi.error('操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    try {
      const res = await deleteAgent({ agent_id: agentId });
      if (res.status === 200) {
        messageApi.success('工具已删除');
        loadAgents();
      }
    } catch (err) {
      messageApi.error('删除失败');
    }
  };

  return {
    agents,
    filteredAgents,
    loading,
    selectedNames,
    setSelectedNames,
    selectedStatus,
    setSelectedStatus,
    nameOptions,
    modalVisible,
    setModalVisible,
    modalMode,
    setModalMode,
    submitting,
    loadAgents,
    handleOpenModal,
    handleSubmitAgent,
    handleDeleteAgent,
  };
};

/**
 * 公告管理 Hook
 */
export const useAnnouncementManagement = (messageApi) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  const loadAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAnnouncements();
      if (res.status === 200) {
        setAnnouncements(res.public_statements || []);
      }
    } catch (err) {
      messageApi.error('加载公告列表失败');
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => { loadAnnouncements(); }, [loadAnnouncements]);

  const handleAddAnnouncement = async (values, form) => {
    try {
      setAddSubmitting(true);
      const res = await addAnnouncement({ public_content: values.public_content });
      if (res.status === 200) {
        messageApi.success('公告发布成功');
        setAddModalVisible(false);
        form.resetFields();
        loadAnnouncements();
      } else {
        messageApi.error(res.msg || '发布失败');
      }
    } catch (err) {
      if (err.errorFields) return;
      messageApi.error('发布失败');
    } finally {
      setAddSubmitting(false);
    }
  };

  // 打开编辑弹窗：记录待编辑公告，具体表单回填由组件层负责
  const handleOpenEditAnnouncement = (record) => {
    setEditingAnnouncement(record);
    setEditModalVisible(true);
  };

  const handleEditAnnouncement = async (values, form) => {
    try {
      setEditSubmitting(true);
      const payload = { id: values.id, public_content: values.public_content };
      if (values.created_time) {
        payload.created_time = dayjs(values.created_time).format('YYYY-MM-DD HH:mm:ss');
      }
      const res = await updateAnnouncement(payload);
      if (res.status === 200) {
        messageApi.success('公告已更新');
        setEditModalVisible(false);
        setEditingAnnouncement(null);
        form.resetFields();
        loadAnnouncements();
      } else {
        messageApi.error(res.msg || '更新失败');
      }
    } catch (err) {
      if (err.errorFields) return;
      messageApi.error('更新失败');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      const res = await deleteAnnouncement({ id });
      if (res.status === 200) {
        messageApi.success('公告已删除');
        loadAnnouncements();
      } else {
        messageApi.error(res.msg || '删除失败');
      }
    } catch (err) {
      messageApi.error('删除失败');
    }
  };

  return {
    announcements,
    loading,
    addModalVisible,
    setAddModalVisible,
    addSubmitting,
    editModalVisible,
    setEditModalVisible,
    editSubmitting,
    editingAnnouncement,
    loadAnnouncements,
    handleAddAnnouncement,
    handleOpenEditAnnouncement,
    handleEditAnnouncement,
    handleDeleteAnnouncement,
  };
};

export { defaultDateRange };
